import React, { useState, Fragment, FormEvent } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { PlusIcon, PencilIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { CandidateWorkHistory } from '../../../Candidate/Types/CandidateType';

interface Props {
  items: CandidateWorkHistory[];
  onChange: (items: CandidateWorkHistory[]) => void;
  maxItems?: number;
}

const emptyItem: CandidateWorkHistory = {
  id: '',
  title: '',
  company: '',
  location: '',
  startDate: null,
  endDate: null,
  isCurrent: false,
  description: '',
  achievements: '',
  createdAt: '',
  updatedAt: '',
};

export const WorkHistoryManager: React.FC<Props> = ({ items, onChange, maxItems = 10 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [form, setForm] = useState<CandidateWorkHistory>(emptyItem);

  const currentList = Array.isArray(items) ? items : [];
  const isLimitReached = maxItems !== undefined && currentList.length >= maxItems;

  const closeModal = () => {
    setIsOpen(false);
    setEditingIndex(null);
    setForm(emptyItem);
  };

  const openAdd = () => {
    setEditingIndex(null);
    setForm(emptyItem);
    setIsOpen(true);
  };

  const openEdit = (index: number) => {
    setEditingIndex(index);
    setForm({ ...currentList[index] });
    setIsOpen(true);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() && !form.company.trim()) return;

    const updated = [...currentList];
    if (editingIndex !== null) {
      updated[editingIndex] = { ...form };
    } else {
      updated.push({ ...form, id: form.id || crypto.randomUUID() });
    }
    onChange(updated);
    closeModal();
  };

  const removeItem = (index: number) => {
    const updated = currentList.filter((_, i) => i !== index);
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-black uppercase tracking-widest text-slate-500">
          Work History {maxItems && `(${currentList.length}/${maxItems})`}
        </h3>
        {!isLimitReached && (
          <button
            type="button"
            onClick={openAdd}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 text-cyan-400 rounded-lg text-xs font-bold hover:bg-slate-700 transition-colors border border-slate-700"
          >
            <PlusIcon className="w-3.5 h-3.5" /> ADD
          </button>
        )}
      </div>

      <div className="space-y-2">
        {currentList.length === 0 ? (
          <p className="text-sm text-slate-500 italic">No work history added yet.</p>
        ) : (
          currentList.map((item, index) => (
            <div
              key={item.id || index}
              className="flex items-start justify-between p-3 rounded-xl bg-slate-900/60 border border-slate-700/60"
            >
              <div>
                <p className="text-sm font-bold text-slate-200">{item.title || 'Untitled role'}</p>
                <p className="text-xs text-slate-400">{item.company}{item.location ? ` · ${item.location}` : ''}</p>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  {item.startDate || 'Start'} → {item.isCurrent ? 'Present' : item.endDate || 'End'}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => openEdit(index)}
                  className="p-1.5 text-slate-400 hover:text-cyan-400 transition-colors"
                >
                  <PencilIcon className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => removeItem(index)}
                  className="p-1.5 text-slate-400 hover:text-rose-400 transition-colors"
                >
                  <XMarkIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <Transition show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[10000]" onClose={closeModal}>
          <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="w-full max-w-2xl bg-slate-900 border border-slate-700 p-6 rounded-2xl shadow-2xl">
              <Dialog.Title className="text-lg font-black text-white mb-4">
                {editingIndex !== null ? 'Edit Role' : 'Add Role'}
              </Dialog.Title>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Title"
                    className="w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                  />
                  <input
                    type="text"
                    placeholder="Company"
                    className="w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                  />
                  <input
                    type="text"
                    placeholder="Location"
                    className="w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                  />
                  <div className="flex items-center gap-3 px-1">
                    <input
                      id="isCurrent"
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-600 bg-slate-950 text-cyan-500 focus:ring-cyan-500"
                      checked={form.isCurrent}
                      onChange={(e) => setForm({ ...form, isCurrent: e.target.checked })}
                    />
                    <label htmlFor="isCurrent" className="text-sm font-bold text-slate-300">Current role</label>
                  </div>
                  <input
                    type="date"
                    placeholder="Start date"
                    className="w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                    value={form.startDate || ''}
                    onChange={(e) => setForm({ ...form, startDate: e.target.value || null })}
                  />
                  {!form.isCurrent && (
                    <input
                      type="date"
                      placeholder="End date"
                      className="w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                      value={form.endDate || ''}
                      onChange={(e) => setForm({ ...form, endDate: e.target.value || null })}
                    />
                  )}
                </div>
                <textarea
                  placeholder="Description"
                  rows={3}
                  className="w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
                <textarea
                  placeholder="Key achievements"
                  rows={2}
                  className="w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                  value={form.achievements}
                  onChange={(e) => setForm({ ...form, achievements: e.target.value })}
                />

                <div className="flex gap-3 justify-end pt-2">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-sm font-bold text-slate-400 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-sm font-bold rounded-lg transition-colors"
                  >
                    Save
                  </button>
                </div>
              </form>
            </Dialog.Panel>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};
